import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  computed,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { signalMethod } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { Chart, ChartConfiguration, ChartType, DefaultDataPoint, Plugin } from 'chart.js';

import { KnxEmpty } from '@optical/ui/empty';
import { KnxSpinner } from '@optical/ui/spinner';
import { knx } from '@optical/ui/utils';

@Component({
  selector: 'knx-chart',
  imports: [KnxEmpty, KnxSpinner, TranslatePipe],
  host: { '[class]': '_computedClass()' },
  templateUrl: './knx-chart.html',
})
export class KnxChart<
  TType extends ChartType = ChartType,
  TData = DefaultDataPoint<TType>,
  TLabel = unknown,
>
  implements OnDestroy, AfterViewInit
{
  private readonly injector = inject(Injector);

  /** The type of the chart */
  type = input<ChartConfiguration<TType, TData, TLabel>['type']>('bar' as TType, {
    alias: 'knxType',
  });
  /** The data for the chart */
  data = input<ChartConfiguration<TType, TData, TLabel>['data']>(
    {
      datasets: [],
      labels: [],
    },
    {
      alias: 'knxData',
    },
  );
  /** The options for the chart */
  options = input.required<ChartConfiguration<TType, TData, TLabel>['options']>({
    alias: 'knxOptions',
  });
  /** The plugins for the chart */
  plugins = input<Plugin<TType>[]>([], {
    alias: 'knxPlugins',
  });

  /** Whether the chart is loading */
  loading = input(false, { alias: 'knxLoading' });
  /** Whether the chart has no data */
  noData = input(false, { alias: 'knxNoData' });

  /** User-provided class for the component */
  userClass = input<string>('', { alias: 'class' });
  /** Computed class property for the component's host element */
  protected readonly _computedClass = computed(() => knx('block', this.userClass(), 'relative'));

  /** The canvas element for the chart */
  canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('chart');
  /** The chart instance */
  chart?: Chart<TType, TData, TLabel>;

  /** Reactive method to render the chart when its configuration changes */
  renderChart = signalMethod<ChartConfiguration<TType, TData, TLabel>>((config) => {
    if (this.chart) {
      // Using assign to avoid changing the original object reference
      if (this.chart) {
        Object.assign(this.chart.config.data, config.data);
        if (this.chart.config.plugins) {
          Object.assign(this.chart.config.plugins, config.plugins);
        }
        if (this.chart.config.options) {
          Object.assign(this.chart.config.options, config.options);
        }
      }

      this.chart.update();

      return;
    }

    const ctx = this.canvas().nativeElement.getContext('2d');
    if (!ctx) throw new Error('No context found');

    this.chart = new Chart(ctx, config);
  });

  /** The configuration object for the chart */
  private chartConfiguration = computed<ChartConfiguration<TType, TData, TLabel>>(() => ({
    type: this.type(),
    data: this.data(),
    options: this.options(),
    plugins: this.plugins(),
  }));

  /**
   * @implements {AfterViewInit}
   */
  ngAfterViewInit(): void {
    this.renderChart(this.chartConfiguration, { injector: this.injector });
  }

  /**
   * @implements {OnDestroy}
   */
  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = void 0;
    }
  }
}
